import { useState, type ReactNode } from 'react';

const REACTION_EMOJIS = ['❤️', '👍', '😮', '😂', '🙏', '✨'];

interface ReactionPickerProps {
  /** The bubble (or bubble + gutter) this picker hovers above. */
  children: ReactNode;
  /** Which side the picker should hug — matches bubble alignment. */
  align: 'left' | 'right';
  onReact: (emoji: string) => void;
  currentReaction?: string;
}

/**
 * Hover wrapper that shows an emoji reaction picker above its children,
 * matching the design references' `[data-bw]`/`[data-rp]` hover-reveal
 * pattern. Purely additive — does not alter the bubble it wraps.
 */
export function ReactionPicker({ children, align, onReact, currentReaction }: ReactionPickerProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {visible && (
        <div className={`absolute bottom-full z-10 pb-1.5 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          <div className="bg-app-card border border-app-border rounded-full shadow-lg flex gap-0.5 px-2 py-1">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  onReact(emoji);
                  setVisible(false);
                }}
                className={`
                  w-7 h-7 flex items-center justify-center rounded-full text-base
                  hover:bg-app-bg active:scale-90 transition-transform
                  ${currentReaction === emoji ? 'bg-gold/10' : ''}
                `}
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export default ReactionPicker;
